import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { dateToLocalString, parseSelectableDate } from "../utils/dateUtils";

interface DatePickerProps {
  value?: string;
  onSelect: (date: string) => void;
  placeholder: string;
  hasError?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabled?: (date: Date) => boolean;
  isDisabled?: boolean;
  /**
   * Taglia dei form dentro le modali (altezza 36px, testo sm), la stessa degli Input
   * `h-9`. Il default resta il campo alto del form pubblico, dove la data e' una delle
   * poche cose sulla pagina.
   */
  compact?: boolean;
  /** Classi aggiuntive sul trigger: serve a smontarne il bordo dentro i box a righe. */
  className?: string;
}

export const DatePicker = ({
  value,
  onSelect,
  placeholder,
  hasError = false,
  minDate,
  maxDate,
  disabled,
  isDisabled = false,
  compact = false,
  className = ''
}: DatePickerProps) => {
  const selected = parseSelectableDate(value);

  return (
  <Popover>
      <PopoverTrigger asChild disabled={isDisabled}>
        <Button
          variant="outline"
          disabled={isDisabled}
          className={`w-full border rounded-md justify-start text-left font-normal hover:bg-gray-100 flex items-center ${
            compact ? 'h-9 px-2.5 py-0 gap-2 text-sm' : 'px-4 py-3 gap-3'
          } ${hasError ? 'border-yellow-400' : 'border-input'} ${className}`}
        >
          <CalendarIcon className={`text-gray-900 shrink-0 ${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
          {selected ? (
            selected.toLocaleDateString('it-IT')
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          /* Senza questo react-day-picker apre sempre sul mese corrente, anche quando la
             data e' gia' valorizzata: per spostare una scadenza di qualche giorno si
             finiva a navigare fin li' ogni volta. Senza valore resta il default (oggi). */
          defaultMonth={selected}
          onSelect={(date) => onSelect(date ? dateToLocalString(date) : "")}
          initialFocus
          disabled={disabled}
          fromDate={minDate}
          toDate={maxDate}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
